import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit3, Layers } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const AdminCollections = () => {
  const [items, setItems] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "", image_url: "", plan_ids: [], is_active: true, start_at: "", end_at: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [colRes, planRes] = await Promise.all([
        api.get("/admin/collections"),
        api.get("/admin/plans?limit=500"),
      ]);
      setItems(colRes.data);
      setAllPlans(planRes.data.items || []);
    } catch (e) { toast.error("Failed to load", { description: e.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ name: "", slug: "", description: "", image_url: "", plan_ids: [], is_active: true, start_at: "", end_at: "" });
    setDialog({ mode: "create" });
  };

  const openEdit = (col) => {
    setForm({
      name: col.name || "", slug: col.slug || "", description: col.description || "",
      image_url: col.image_url || "", plan_ids: col.plan_ids || [],
      is_active: col.is_active !== false, start_at: col.start_at || "", end_at: col.end_at || "",
    });
    setDialog({ mode: "edit", col });
  };

  const remove = async (col) => {
    if (!window.confirm(`Delete collection "${col.name}"?`)) return;
    try { await api.delete(`/admin/collections/${col.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed", { description: e.response?.data?.detail || "" }); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug ? slugify(form.slug) : slugify(form.name), start_at: form.start_at || null, end_at: form.end_at || null };
      if (dialog.mode === "create") { await api.post("/admin/collections", payload); toast.success("Collection created"); }
      else { await api.patch(`/admin/collections/${dialog.col.id}`, payload); toast.success("Collection updated"); }
      setDialog(null); load();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  const togglePlan = (pid) => {
    setForm((f) => {
      const s = new Set(f.plan_ids);
      if (s.has(pid)) s.delete(pid); else s.add(pid);
      return { ...f, plan_ids: Array.from(s) };
    });
  };

  return (
    <AdminLayout title="Collections" actions={
      <Button data-testid="col-new-btn" onClick={openCreate} className="rounded-none bg-accent hover:bg-accent/90"><Plus size={14} className="mr-2" /> New collection</Button>
    }>
      <div className="border border-border bg-card">
        <div className="px-4 py-3 border-b border-border bg-background flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
          <Layers size={12} /> Curated collections
        </div>
        {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-12 text-center text-sm text-muted-foreground" data-testid="collections-empty">No collections yet — try &ldquo;Modern Homes&rdquo;, &ldquo;Under 2000 Sqft&rdquo;, &ldquo;Best Sellers&rdquo;.</div>}
        {!loading && items.map((c) => (
          <div key={c.id} data-testid={`col-row-${c.id}`} className="grid grid-cols-1 md:grid-cols-[1fr_140px_120px_120px_120px] gap-3 items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-background/60">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">/{c.slug} · {c.plan_ids?.length || 0} plans</div>
            </div>
            <div className="text-xs">{c.is_active ? "Active" : "Hidden"}</div>
            <div className="text-[11px] text-muted-foreground">{c.start_at ? `From ${new Date(c.start_at).toLocaleDateString()}` : "—"}</div>
            <div className="text-[11px] text-muted-foreground">{c.end_at ? `Until ${new Date(c.end_at).toLocaleDateString()}` : "—"}</div>
            <div className="flex gap-3 justify-end">
              <button data-testid={`col-edit-${c.id}`} onClick={() => openEdit(c)} className="text-xs text-muted-foreground hover:text-accent"><Edit3 size={12} /></button>
              <button data-testid={`col-delete-${c.id}`} onClick={() => remove(c)} className="text-xs text-muted-foreground hover:text-destructive"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog?.mode === "create" ? "New collection" : "Edit collection"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline text-muted-foreground text-[10px]">Name</Label><Input data-testid="col-name-input" className="rounded-none mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) }))} /></div>
              <div><Label className="overline text-muted-foreground text-[10px]">Slug</Label><Input data-testid="col-slug-input" className="rounded-none mt-1" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} /></div>
            </div>
            <div><Label className="overline text-muted-foreground text-[10px]">Description</Label><Textarea data-testid="col-desc-input" rows={2} className="rounded-none mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">Image URL</Label><Input data-testid="col-image-input" className="rounded-none mt-1" value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline text-muted-foreground text-[10px]">Start date</Label><Input data-testid="col-start-input" type="date" className="rounded-none mt-1" value={form.start_at ? form.start_at.slice(0, 10) : ""} onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))} /></div>
              <div><Label className="overline text-muted-foreground text-[10px]">End date</Label><Input data-testid="col-end-input" type="date" className="rounded-none mt-1" value={form.end_at ? form.end_at.slice(0, 10) : ""} onChange={(e) => setForm((f) => ({ ...f, end_at: e.target.value }))} /></div>
            </div>
            <div className="flex items-center gap-2"><Checkbox data-testid="col-active-input" checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: !!v }))} /><Label>Active</Label></div>
            <div>
              <Label className="overline text-muted-foreground text-[10px]">Plans in this collection ({form.plan_ids.length})</Label>
              <div className="mt-2 border border-border max-h-60 overflow-y-auto" data-testid="col-plan-picker">
                {allPlans.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 cursor-pointer hover:bg-background">
                    <Checkbox checked={form.plan_ids.includes(p.id)} onCheckedChange={() => togglePlan(p.id)} data-testid={`col-plan-toggle-${p.id}`} />
                    <span className="text-sm">{p.title}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{p.style} · {p.status}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialog(null)}>Cancel</Button>
            <Button data-testid="col-save-btn" className="rounded-none bg-accent hover:bg-accent/90" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCollections;
